import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Section } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";
import { getServiceLocationPage } from "@/lib/services.functions";

const pageQ = (location: string, service: string) => ({
  queryKey: ["service-location", location, service],
  queryFn: () => getServiceLocationPage({ data: { location_slug: location, service_slug: service } }),
  staleTime: 300_000,
});

function mdToHtml(md: string): string {
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  let inOl = false;
  const close = () => { if (inList) { html += "</ul>"; inList = false; } if (inOl) { html += "</ol>"; inOl = false; } };
  for (const raw of lines) {
    const l = raw.trim();
    if (l.startsWith("# ")) { close(); html += `<h1 class="text-3xl md:text-4xl font-bold tracking-tight mt-8 mb-4">${l.slice(2)}</h1>`; }
    else if (l.startsWith("## ")) { close(); html += `<h2 class="text-2xl font-bold mt-8 mb-3">${l.slice(3)}</h2>`; }
    else if (/^\d+\.\s/.test(l)) { if (!inOl) { close(); html += `<ol class="list-decimal ml-6 space-y-1 my-3">`; inOl = true; } html += `<li>${l.replace(/^\d+\.\s/, "")}</li>`; }
    else if (l.startsWith("- ")) { if (!inList) { close(); html += `<ul class="list-disc ml-6 space-y-1 my-3">`; inList = true; } html += `<li>${l.slice(2)}</li>`; }
    else if (l === "") { close(); }
    else { close(); html += `<p class="my-3 text-muted-foreground leading-relaxed">${l}</p>`; }
  }
  close();
  return html;
}

export const Route = createFileRoute("/$location/$service")({
  loader: async ({ context, params }) => {
    const row = await context.queryClient.ensureQueryData(pageQ(params.location, params.service));
    if (!row) throw notFound();
    return { row };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Página não encontrada" }, { name: "robots", content: "noindex" }] };
    const t = seoTitle(loaderData.row.title);
    const d = seoDescription(loaderData.row.description);
    const url = `/${params.location}/${params.service}`;
    const meta = [
      { title: t }, { name: "description", content: d },
      { property: "og:title", content: t }, { property: "og:description", content: d }, { property: "og:url", content: url },
    ];
    if (loaderData.row.cover_image) meta.push({ property: "og:image", content: loaderData.row.cover_image });
    return { meta, links: [{ rel: "canonical", href: `${SITE.baseUrl}${url}` }] };
  },
  component: LocationServicePage,
  errorComponent: ({ error }) => <div className="container-svg py-20 text-center">Erro: {String(error)}</div>,
  notFoundComponent: () => (
    <div className="container-svg py-20 text-center">
      <h1 className="text-2xl font-bold">Página não encontrada</h1>
      <Link to="/servicos" className="mt-4 inline-block btn-primary">Ver serviços</Link>
    </div>
  ),
});

function LocationServicePage() {
  const { location, service } = Route.useParams();
  const { data } = useSuspenseQuery(pageQ(location, service));
  if (!data) return null;
  const html = mdToHtml(data.body || "");
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-10 pb-10 md:pt-16 md:pb-14">
          <span className="eyebrow">{data.neighborhood ? `${data.neighborhood} · Chapecó` : `${data.city} · SC`}</span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight"><span className="text-gradient">{data.service_label}</span> em {data.neighborhood ?? data.city}</h1>
          <p className="mt-4 max-w-2xl text-muted-foreground md:text-lg">{data.description}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent px-6 py-3">Orçamento no WhatsApp</a>
            <Link to="/contato" className="btn-ghost px-6 py-3">Agendar vistoria</Link>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <article className="prose-content" dangerouslySetInnerHTML={{ __html: html }} />
          <aside className="space-y-4">
            {data.cover_image ? <img src={data.cover_image} alt={data.title} className="w-full rounded border border-border" loading="lazy" /> : null}
            <div className="card-glass">
              <h3 className="font-bold">Fale agora</h3>
              <p className="mt-2 text-sm text-muted-foreground">Atendimento rápido pelo WhatsApp — orçamento gratuito.</p>
              <a href={SITE.whatsappUrl} target="_blank" rel="noopener" className="btn-accent mt-4 w-full block text-center">WhatsApp {SITE.phone}</a>
            </div>
            <div className="card-glass">
              <h3 className="font-bold">Outros serviços</h3>
              <ul className="mt-3 space-y-1 text-sm">
                {["dedetizacao","controle-de-roedores","controle-de-escorpiao","controle-de-cupim","controle-de-baratas","controle-de-pragas-24h"].filter(s => s !== service).map(s => (
                  <li key={s}><Link to="/$location/$service" params={{ location, service: s }} className="text-primary hover:underline">Ver este serviço em {data.neighborhood ?? data.city}</Link></li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </Section>

      <CTASection title={`Precisa de ${data.service_label.toLowerCase()} em ${data.neighborhood ?? data.city}?`} text="Fale conosco pelo WhatsApp agora." buttonLabel="Falar no WhatsApp" />
    </>
  );
}
